import { TBlkCxpRole } from '../../types/settingsDataModule';

/**
 * DEV-ONLY auth stub.
 *
 * Mints a syntactically valid (but unsigned) JWT so the app can be browsed
 * without a real Keycloak session. `jwt-decode` only base64-decodes the
 * payload — it never verifies the signature — so this is enough to satisfy
 * UserProfileService and the route guards.
 *
 * Real API calls will still fail (the backend rejects this token); this only
 * unlocks the UI shell for front-end work. Gated behind
 * `environment.devBypassAuth`, which is false everywhere except development.
 */

const DEV_INSTITUTION_NAME = 'Black Konnect (Dev)';
const DEV_ORG_ID = '00000000-0000-4000-8000-000000000001';
const DEV_KEYCLOAK_ID = '11111111-1111-4111-8111-111111111111';

/** Every CXP role, so no permission-gated UI is hidden while exploring. */
export const DEV_ROLES: TBlkCxpRole[] = [
  'default-roles-blackkonnect',
  'SUPER_ADMIN',
  'ROLE_BLKKCXP_ADMINISTRATOR',
  'ROLE_BLKKCXP_ACCOUNT_MANAGER',
  'ROLE_BLKKCXP_CONTACTS_MANAGER',
  'ROLE_BLKKCXP_CONTENT_MANAGER',
  'ROLE_BLKKCXP_INTEGRATIONS_MANAGER',
  'ROLE_BLKKCXP_FINANCE_DATA_MANAGER',
];

function base64Url(value: string): string {
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function makeDevJwt(): string {
  const issuedAt = Math.floor(Date.now() / 1000);

  const header = { alg: 'none', typ: 'JWT' };
  const payload = {
    sub: DEV_KEYCLOAK_ID,
    name: 'Dev User',
    given_name: 'Dev',
    family_name: 'User',
    preferred_username: 'dev.user',
    email: 'dev.user@blackkonnect.local',
    email_verified: true,
    organisationId: DEV_ORG_ID,
    organization: {
      [DEV_INSTITUTION_NAME]: {
        id: DEV_KEYCLOAK_ID,
        orgId: [DEV_ORG_ID],
      },
    },
    realm_access: { roles: DEV_ROLES },
    iat: issuedAt,
    // Far-future expiry so the session never lapses mid-session.
    exp: issuedAt + 60 * 60 * 24 * 365,
  };

  return `${base64Url(JSON.stringify(header))}.${base64Url(
    JSON.stringify(payload)
  )}.dev-stub-signature`;
}
